<template>
  <div class="vue-modal" v-show="open" @click="$emit('close')">
    <button v-if="showClose" type="button" class="f20-l30-b pointer vue-modal-close" @click="$emit('close')" aria-label="סגור">X</button>
    <div
      ref="modalPanel"
      role="dialog"
      :aria-modal="externalPaymentOpen ? 'false' : 'true'"
      :aria-labelledby="dialogAriaLabelledby"
      :aria-describedby="dialogAriaDescribedby"
      :class="`f16-l19 d-flex flex-column  vue-modal-inner-${variant2} vue-model-inner-${colorVariant} ${containerClass}`"
      @click.stop="false"
    >
      <div id="modalHeader" dir="rtl">
        <div v-if="hasHeaderSlot" id="modalTitle">
          <slot name="header" />
        </div>
      </div>
      <div class="flex-grow-1 vue-modal-content" @click.stop="false">
        <slot />
      </div>
      <div id="modalFooter" dir="rtl">
        <slot name="footer" />
      </div>
    </div>
  </div>
</template>
<script>
/* global $ */
import { GROW_WALLET_CHANGE_EVENT, isGrowWalletFocusContext } from '@/modules/growSdk.js';
import { TRANZILA_OVERLAY_CHANGE_EVENT } from '@/modules/tranzilaHostedFields.js';
import { POPUP_A11Y_REGISTER_KEY } from '@/constants/popupA11y.js';

export default {
  provide() {
    return {
      [POPUP_A11Y_REGISTER_KEY]: this.registerPopupA11y,
    };
  },
  props: {
    open: {
      type: Boolean,
      default: true,
    },
    showClose: {
      type: Boolean,
      default: true,
    },
    containerClass: {
      type: String,
      default: 'mw820'
    },
    variant: {
      type: String,
      default: 'center',
      validator(value) {
        return ['center', 'bottom', 'top-center'].includes(value)
      }
    },
    colorVariant: {
      type: String,
      default: 'white',
      validator(value){
        return ['white', 'red'].includes(value)
      }
    }

  },
  data() {
    return {
      previousFocus: null,
      keydownHandler: null,
      growWalletChangeHandler: null,
      tranzilaOverlayHandler: null,
      focusMoved: false,
      growWalletOpen: false,
      tranzilaOverlayOpen: false,
      popupA11yMeta: { titleId: null, describedBy: null },
    }
  },
  computed: {
    variant2() {
       return this.$isIOS() ? 'top-center' : this.variant;
    },
    hasHeaderSlot() {
      return Boolean(this.$slots.header);
    },
    externalPaymentOpen() {
      return this.growWalletOpen || this.tranzilaOverlayOpen;
    },
    dialogAriaLabelledby() {
      if (this.hasHeaderSlot) {
        return 'modalTitle';
      }
      return this.popupA11yMeta?.titleId || undefined;
    },
    dialogAriaDescribedby() {
      return this.popupA11yMeta?.describedBy || undefined;
    },
  },
  mounted() {
    $("body").addClass('noscroll popupisopen');
    this.previousFocus = document.activeElement;
    this.keydownHandler = (e) => this.handleKeydown(e);
    document.addEventListener('keydown', this.keydownHandler);
    this.growWalletChangeHandler = (e) => {
      this.growWalletOpen = Boolean(e.detail?.open);
      if (!this.growWalletOpen) {
        this.focusMoved = false;
        this.$nextTick(() => this.moveFocusIntoModal());
      }
    };
    document.addEventListener(GROW_WALLET_CHANGE_EVENT, this.growWalletChangeHandler);
    this.tranzilaOverlayHandler = (e) => {
      this.tranzilaOverlayOpen = Boolean(e.detail?.open);
      if (!this.tranzilaOverlayOpen) {
        this.focusMoved = false;
        this.$nextTick(() => this.moveFocusIntoModal());
      }
    };
    document.addEventListener(TRANZILA_OVERLAY_CHANGE_EVENT, this.tranzilaOverlayHandler);
    this.$nextTick(() => this.moveFocusIntoModal());
  },
  updated() {
    if (!this.focusMoved && !this.externalPaymentOpen) {
      this.$nextTick(() => this.moveFocusIntoModal());
    }
  },
  unmounted() {
    if (this.keydownHandler) {
      document.removeEventListener('keydown', this.keydownHandler);
    }
    if (this.growWalletChangeHandler) {
      document.removeEventListener(GROW_WALLET_CHANGE_EVENT, this.growWalletChangeHandler);
    }
    if (this.tranzilaOverlayHandler) {
      document.removeEventListener(TRANZILA_OVERLAY_CHANGE_EVENT, this.tranzilaOverlayHandler);
    }
    this.growWalletOpen = false;
    this.tranzilaOverlayOpen = false;
    this.popupA11yMeta = { titleId: null, describedBy: null };
    $("body").removeClass('noscroll popupisopen');
    const restore = this.previousFocus;
    if (restore && typeof restore.focus === 'function') {
      this.$nextTick(() => restore.focus());
    }
  },
  methods: {
    registerPopupA11y(meta) {
      this.popupA11yMeta = meta || { titleId: null, describedBy: null };
    },
    shouldTrapFocus() {
      if (this.externalPaymentOpen) {
        return false;
      }
      return !isGrowWalletFocusContext();
    },
    getFocusableElements() {
      const panel = this.$refs.modalPanel;
      if (!panel) {
        return [];
      }
      const selector = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
      return [...panel.querySelectorAll(selector)].filter((el) => {
        return el.getClientRects().length > 0;
      });
    },
    moveFocusIntoModal() {
      if (!this.shouldTrapFocus()) {
        return;
      }
      const focusable = this.getFocusableElements();
      if (focusable.length) {
        focusable[0].focus();
        this.focusMoved = true;
        return;
      }
      const panel = this.$refs.modalPanel;
      if (panel) {
        panel.setAttribute('tabindex', '-1');
        panel.focus();
        this.focusMoved = true;
      }
    },
    handleKeydown(e) {
      if (e.key === 'Escape') {
        if (this.externalPaymentOpen) {
          return;
        }
        e.preventDefault();
        this.$emit('close');
        return;
      }
      if (!this.shouldTrapFocus()) {
        return;
      }
      if (e.key !== 'Tab') {
        return;
      }
      const panel = this.$refs.modalPanel;
      const focusable = this.getFocusableElements();
      if (!focusable.length) {
        e.preventDefault();
        this.moveFocusIntoModal();
        return;
      }
      const focusInPanel = panel && panel.contains(document.activeElement);
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey) {
        if (!focusInPanel || document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else if (!focusInPanel || document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    },
  }

}
</script>
<style scoped>
/* *,
::before,
::after {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
} */

.vue-modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  overflow-x: hidden;
  overflow-y: auto;
  background-color: rgba(0, 0, 0, 0.4);
  z-index: 3000;
}

.vue-modal-inner-top-center {
  margin: 2rem auto;
  overflow-y: hidden;
  background-color: #fff;
  border-radius: 20px;
  background-clip: padding-box;
  border: 1px solid rgba(0, 0, 0, 0.3);
  max-height: 80%;
}


.vue-modal-inner-center {
  margin: 8rem auto;
  overflow-y: hidden;
  background-color: #fff;
  border-radius: 20px;
  background-clip: padding-box;
  border: 1px solid rgba(0, 0, 0, 0.3);
  max-height: 80%;
  /* position:fixed;  */
  /* top: calc(50% -  );  */
  /* left: calc(50% - 300px);  */
}

.vue-model-inner-white{
  background-color: #fff;
}
.vue-model-inner-red {
  background-color: rgb(239, 61, 73);;
}

.vue-modal-inner-bottom {
  /* max-width: 500px; */
  max-height: 80%;
  /* margin: 2rem auto; */
  width: 100%;
  /* overflow-y: auto;
  position: fixed; */
  bottom: 0;
  background-color: #fff;
  border-top-left-radius: 20px;
  border-top-right-radius: 20px;
  background-clip: padding-box;
  border: 1px solid rgba(0, 0, 0, 0.3);
  position: fixed;
  bottom: 0;
}

@media (min-width: 768px) {
  .vue-modal-inner-bottom {
    left: 50%;
    left: 50% !important;
    -webkit-transform: translateX(-50%);
    -moz-transform: translateX(-50%);
    transform: translateX(-50%);
  }
}

.vue-modal-content {
  position: relative;
  /* background-color: green; */
  overflow-y: auto;
  padding: 1rem;
}

.vue-modal-inner-center.tranzila-hf-popup,
.vue-modal-inner-top-center.tranzila-hf-popup {
  overflow: visible;
  max-height: 92%;
}

.vue-modal-inner-bottom .vue-modal-content {
  border-width: 0;
}

.vue-modal-close {
  border: none;
  background: transparent;
  padding: 0;
  line-height: inherit;
  font: inherit;
  color: #383838;
  cursor: pointer;
}
</style>
